import React, { useCallback, useRef } from 'react'

import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import {
  CategoricalShareLegend,
  GroupedBarsGroups,
  StackedHundredBarsRows,
  canOpenCategoricalOther,
  otherFold,
  readMatrixCategoricalSlide,
} from '../plots/categorical-share'
import { OtherToggle, isOtherExpandable, useOtherExpand } from '../plots/_shared/OtherExpand'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { parseInlineBold } from '../primitives'
import { EditableSlideText } from './EditableSlideText'
import { registerSlide } from './registry'
import { RailLayout } from '../layouts/RailLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative } from '../slots'
import type { EditableSlideProps } from './contracts'
import type { MatrixCategoricalBarsSlideData } from './renderer-internal-types'
import type { CategoricalShareKind, CategoricalSharePlotData, StoredMatrixCategoricalSlide } from '../plots/categorical-share'

const TEXT = 'var(--slide-cover-bg)'
const PANEL = 'var(--slide-card-bg)'
const MATRIX_CATEGORICAL_SLIDE_TYPE = 'matrix-categorical-bars'
const ACTION_SUBTITLE_CLAMP = schemaBackedProseClampProps(
  'actionSubtitle',
  MATRIX_CATEGORICAL_SLIDE_TYPE,
  { lineHeight: '1.5' },
)
const TITLE_CLAMP = schemaBackedProseClampProps('title', MATRIX_CATEGORICAL_SLIDE_TYPE, {
  lineHeight: '1.2',
})
/** Space kept between the rows and the legend when an opened Other grows. */
const OTHER_GROW_GAP = 16
/**
 * The stacked link sits in the panel's bottom padding, right-aligned, so the
 * bottom-anchored legend keeps its closed position and never climbs into the rows.
 */
const STACKED_OTHER_LINK_BOTTOM = 2

/**
 * The slide draws its `plot` (plots plan L): `stacked-100-bars` for a grid or a
 * single-select lane exhibit, `grouped-bars` (options x assets) for a
 * multi-select lane exhibit, whose shares of one asset sum past 100 and are
 * never re-normalised. A slide stored before `plot` draws through the legacy
 * read adapter. The panel, the rows' metrics and the legend keep their place.
 */
const HEADLINE = presetSlot(MATRIX_CATEGORICAL_SLIDE_TYPE, 'headline').text!
const NARRATIVE = presetSlot(MATRIX_CATEGORICAL_SLIDE_TYPE, 'narrative').text!
const BULLETS = presetSlot(MATRIX_CATEGORICAL_SLIDE_TYPE, 'bullets').text!

function MatrixCategoricalBarsSlideComponent(
  props: MatrixCategoricalBarsSlideData & EditableSlideProps & { plot?: StoredMatrixCategoricalSlide['plot']; locale?: string | null },
) {
  // D17: forwarded to every clamp that offers a truncation tooltip. In a static export
  // (the headless-browser PDF pass) a Radix trigger can never open, so those clamps fall
  // back to the native `title`, which is the only carrier a printed page can hold.
  const { editable, onEdit, renderMode, plotOtherToggleLabels } = props
  const rowsRef = useRef<HTMLDivElement>(null)
  const legendRef = useRef<HTMLDivElement>(null)
  // Opened, the box grows into the free room between the rows and the
  // bottom-anchored legend (then scrolls): the legend upward on a stacked
  // exhibit, the groups downward on a grouped one.
  const growRoom = useCallback(() => {
    const rows = rowsRef.current
    const legend = legendRef.current
    if (!rows || !legend) {
      return 0
    }
    return legend.offsetTop - (rows.offsetTop + rows.offsetHeight) - OTHER_GROW_GAP
  }, [])
  const other = useOtherExpand({ growRoom })
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const { payload, isStamped } = readMatrixCategoricalSlide({
    type: 'matrix-categorical-bars',
    categories: props.categories || [],
    rows: props.rows || [],
    page: props.page ?? null,
    plot: props.plot,
  })
  const kind = payload.kind as CategoricalShareKind
  const data = payload.data as CategoricalSharePlotData
  const labelField = isStamped ? 'plot.data.rows' : 'rows'
  // Decision 23: the viewer's active slide opens "Other" in place (the panel
  // keeps its geometry). A static render passes no words and draws the Other bar only.
  const canOpen = isOtherExpandable(renderMode, plotOtherToggleLabels, canOpenCategoricalOther(data))
  const otherOpen = canOpen && other.isOpen
  const otherLink =
    canOpen && plotOtherToggleLabels ? (
      <OtherToggle
        state={other}
        labels={plotOtherToggleLabels}
        count={otherFold(data)?.total ?? data.categories.length}
        testIdPrefix="matrix-categorical"
        style={kind === 'grouped-bars' ? { marginTop: 8 } : undefined}
      />
    ) : null
  const page = props.page && props.page.total > 1 ? props.page : null
  const eyebrow = page?.label
    ? [props.eyebrow, page.label].filter(Boolean).join(' · ')
    : // legacy reader only: a deck built since AI part 3 stamps `page.label`.
      `${props.eyebrow || ''}${props.eyebrow?.includes('Page') ? '' : page ? ` · Page ${page.index}/${page.total}` : ''}`.trim()
  return (
    <RailLayout
      preset={MATRIX_CATEGORICAL_SLIDE_TYPE}
      eyebrow={eyebrow}
      clientLogoUrl={logoUrl}
      testIds={{ layout: 'matrix-categorical-bars-layout', plot: 'matrix-categorical-bars-panel', rail: 'matrix-categorical-narrative' }}
      footerText={props.footerText}
      plotStyle={{ borderRadius: 16, background: PANEL, overflow: 'hidden' }}
      plot={
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="matrix-categorical-bars"
            columns={data.categories.map((category) => ({ id: category.id, label: category.label }))}
            rows={data.rows.map((row) => ({ id: row.id, label: row.label }))}
            categories={data.categories.map((category) => ({ id: category.id, label: category.label }))}
            title={props.title || ''}
          >
          <ChartSegmentTitleSlot
            contentPadRight={0}
            style={{ position: 'absolute', left: 31, top: 32, right: 0 }}
          >
            <EditableSlideText
              data-testid="matrix-categorical-bars-panel-title"
              value={props.title || ''}
              field="title"
              editable={editable}
              onEdit={onEdit}
              tooltipOnTruncate
              renderMode={renderMode}
              // The question, whole: three 14px lines end above the rows at top 112.
              minLines={TITLE_CLAMP.minLines}
              maxLines={TITLE_CLAMP.maxLines}
              lineHeight={TITLE_CLAMP.lineHeight}
              style={{ fontSize: 14, fontWeight: 500, color: 'var(--slide-text-primary)' }}
            />
          </ChartSegmentTitleSlot>
          <div ref={legendRef} data-testid="matrix-categorical-legend" style={{ position: 'absolute', left: 31, right: 31, bottom: 20 }}>
            {otherLink && kind !== 'grouped-bars' ? (
              <div ref={other.boxRef} id={other.boxId} style={other.boxStyle}>
                <CategoricalShareLegend data={data} kind={kind} size="slide" testIdPrefix="matrix-categorical" renderMode={renderMode} otherOpen={otherOpen} />
              </div>
            ) : (
              <CategoricalShareLegend data={data} kind={kind} size="slide" testIdPrefix="matrix-categorical" renderMode={renderMode} />
            )}
          </div>
          {otherLink && kind !== 'grouped-bars' ? (
            <div style={{ position: 'absolute', right: 31, bottom: STACKED_OTHER_LINK_BOTTOM, display: 'flex' }}>{otherLink}</div>
          ) : null}

          <div ref={rowsRef} data-testid={`matrix-categorical-${kind}`} style={{ position: 'absolute', left: 31, right: 31, top: 112 }}>
            {kind === 'grouped-bars' && otherLink ? (
              <>
                <div ref={other.boxRef} id={other.boxId} style={other.boxStyle}>
                  <GroupedBarsGroups data={data} size="slide" locale={props.locale ?? undefined} renderMode={renderMode} otherOpen={otherOpen} testIdPrefix="matrix-categorical" />
                </div>
                {otherLink}
              </>
            ) : kind === 'grouped-bars' ? (
              <GroupedBarsGroups data={data} size="slide" locale={props.locale ?? undefined} renderMode={renderMode} />
            ) : (
              <StackedHundredBarsRows
                data={data}
                size="slide"
                locale={props.locale ?? undefined}
                testIdPrefix="matrix-categorical"
                renderMode={renderMode}
                otherOpen={otherOpen}
                renderRowLabel={({ row, index, style, chartLabel }) => (
                  <EditableSlideText
                    data-testid="matrix-categorical-row-label"
                    value={row.label}
                    field={`${labelField}.${index}.label`}
                    editable={editable}
                    onEdit={onEdit}
                    tooltipOnTruncate
                    chartLabel={chartLabel}
                    renderMode={renderMode}
                    minLines={1}
                    maxLines={1}
                    lineHeight="1.2"
                    style={{ ...style, color: TEXT }}
                  />
                )}
              />
            )}
          </div>
          </PlotBox>
      }
    >
          <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} width={HEADLINE.width} editable={editable} onEdit={onEdit} />
          {props.actionSubtitle ? (
            <SlotNarrative
              value={props.actionSubtitle}
              clamp={ACTION_SUBTITLE_CLAMP}
              testId="matrix-categorical-action-subtitle"
              style={{ marginTop: 24, width: NARRATIVE.width }}
              renderMode={renderMode}
            >
              {parseInlineBold(props.actionSubtitle)}
            </SlotNarrative>
          ) : null}
          {props.insightBullets?.length ? (
            <SlotBullets
              bullets={props.insightBullets}
              fontSize={BULLETS.fontSize}
              gap={8}
              maxLines={BULLETS.maxLines}
              dotColor="var(--slide-accent)"
              testId="matrix-categorical-insight-bullets"
              // The list is the text box plus the 8px dot and its 12px gap.
              style={{ marginTop: 16, width: BULLETS.width + 20 }}
              editable={editable}
              onEdit={onEdit}
              renderMode={renderMode}
            />
          ) : null}
    </RailLayout>
  )
}

export const MatrixCategoricalBarsSlide = registerSlide('matrix-categorical-bars', MatrixCategoricalBarsSlideComponent)
